import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  LicenseState,
  LicenseStatus,
  LicenseTier,
  ProFeatures,
  initialLicenseState,
  getTierFeatures,
  FREE_TIER_DAILY_ROUTE_LIMIT,
} from "../types/licenseTypes";
import {
  validateLicense,
  activateLicense,
  deactivateLicense,
  getStoredLicenseKey,
  storeLicenseKey,
  clearLicenseKey,
  canPerformRouteSearch,
  incrementDailyRouteCount,
  getDailyRouteCount,
} from "../services/licenseService";

// License actions
type LicenseAction =
  | { type: "SET_LOADING"; payload: boolean }
  | {
      type: "SET_LICENSE_VALID";
      payload: {
        tier: LicenseTier;
        expiresAt: string;
        domainsRemaining: number;
      };
    }
  | { type: "SET_LICENSE_INVALID"; payload: string | null }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "RESET_LICENSE" };

// License reducer
const licenseReducer = (
  state: LicenseState,
  action: LicenseAction,
): LicenseState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_LICENSE_VALID":
      return {
        ...state,
        isLoading: false,
        isValidated: true,
        isPro: action.payload.tier !== "free",
        tier: action.payload.tier,
        status: "active" as LicenseStatus,
        expiresAt: action.payload.expiresAt,
        domainsRemaining: action.payload.domainsRemaining,
        error: null,
      };
    case "SET_LICENSE_INVALID":
      return {
        ...state,
        isLoading: false,
        isValidated: false,
        isPro: false,
        tier: "free",
        status: "invalid" as LicenseStatus,
        expiresAt: null,
        domainsRemaining: 0,
        error: action.payload,
      };
    case "SET_ERROR":
      return { ...state, isLoading: false, error: action.payload };
    case "RESET_LICENSE":
      return { ...initialLicenseState, isLoading: false };
    default:
      return state;
  }
};

// Context value interface
interface LicenseContextValue {
  // State
  licenseState: LicenseState;
  features: ProFeatures;

  // Actions
  checkLicense: () => Promise<void>;
  activateLicenseKey: (licenseKey: string) => Promise<boolean>;
  deactivateLicenseKey: () => Promise<boolean>;

  // Route limiting
  canSearchRoutes: () => { allowed: boolean; remaining: number };
  recordRouteSearch: () => void;
  dailyRouteCount: number;
  dailyRouteLimit: number;
}

// Create context
const LicenseContext = createContext<LicenseContextValue | undefined>(
  undefined,
);

// Provider props
interface LicenseProviderProps {
  children: ReactNode;
}

// License provider component
export const LicenseProvider: React.FC<LicenseProviderProps> = ({
  children,
}) => {
  const [licenseState, dispatch] = useReducer(
    licenseReducer,
    initialLicenseState,
  );

  // Get features based on current tier
  const features = getTierFeatures(licenseState.tier);

  // Check stored license on mount
  const checkLicense = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });

    // First check if there's a license key in WordPress settings
    const wpLicenseKey = window.trpSettings?.licenseKey;
    const storedKey = getStoredLicenseKey();
    const licenseKey = wpLicenseKey || storedKey;

    if (!licenseKey) {
      // No license key stored, use free tier
      dispatch({ type: "SET_LICENSE_INVALID", payload: null });
      return;
    }

    // Validate the license key
    const result = await validateLicense(licenseKey);

    if (result.valid) {
      dispatch({
        type: "SET_LICENSE_VALID",
        payload: {
          tier: result.tier,
          expiresAt: result.expires_at,
          domainsRemaining: result.domains_remaining,
        },
      });

      // Store the key if it came from WordPress settings
      if (wpLicenseKey && !storedKey) {
        storeLicenseKey(wpLicenseKey);
      }
    } else {
      dispatch({
        type: "SET_LICENSE_INVALID",
        payload: result.error || "Invalid license key",
      });
    }
  }, []);

  // Activate a new license key
  const activateLicenseKey = useCallback(
    async (licenseKey: string): Promise<boolean> => {
      dispatch({ type: "SET_LOADING", payload: true });

      const result = await activateLicense(licenseKey);

      if (result.success && result.license_info) {
        dispatch({
          type: "SET_LICENSE_VALID",
          payload: {
            tier: result.license_info.tier,
            expiresAt: result.license_info.expires_at,
            domainsRemaining:
              result.license_info.max_domains -
              result.license_info.domains.length,
          },
        });
        return true;
      } else {
        dispatch({
          type: "SET_ERROR",
          payload: result.error || "Failed to activate license",
        });
        return false;
      }
    },
    [],
  );

  // Deactivate the current license
  const deactivateLicenseKey = useCallback(async (): Promise<boolean> => {
    const licenseKey = getStoredLicenseKey();
    if (!licenseKey) {
      dispatch({ type: "RESET_LICENSE" });
      return true;
    }

    dispatch({ type: "SET_LOADING", payload: true });

    const result = await deactivateLicense(licenseKey);

    if (result.success) {
      clearLicenseKey();
      dispatch({ type: "RESET_LICENSE" });
      return true;
    } else {
      dispatch({
        type: "SET_ERROR",
        payload: result.error || "Failed to deactivate license",
      });
      return false;
    }
  }, []);

  // Check if route search is allowed
  const canSearchRoutes = useCallback(() => {
    return canPerformRouteSearch(
      licenseState.isPro,
      FREE_TIER_DAILY_ROUTE_LIMIT,
    );
  }, [licenseState.isPro]);

  // Record a route search
  const recordRouteSearch = useCallback(() => {
    if (!licenseState.isPro) {
      incrementDailyRouteCount();
    }
  }, [licenseState.isPro]);

  // Get current daily route count
  const dailyRouteCount = getDailyRouteCount();

  // Check license on mount
  useEffect(() => {
    checkLicense();
  }, [checkLicense]);

  const contextValue: LicenseContextValue = {
    licenseState,
    features,
    checkLicense,
    activateLicenseKey,
    deactivateLicenseKey,
    canSearchRoutes,
    recordRouteSearch,
    dailyRouteCount,
    dailyRouteLimit: FREE_TIER_DAILY_ROUTE_LIMIT,
  };

  return (
    <LicenseContext.Provider value={contextValue}>
      {children}
    </LicenseContext.Provider>
  );
};

// Custom hook to use license context
export const useLicense = (): LicenseContextValue => {
  const context = useContext(LicenseContext);
  if (context === undefined) {
    throw new Error("useLicense must be used within a LicenseProvider");
  }
  return context;
};

export default LicenseContext;
